// ErrorBoundary: catches render/lifecycle errors in its subtree so one broken
// tab can't blank the whole shell. React only exposes this as a class API
// (getDerivedStateFromError + componentDidCatch), so this is deliberately a
// class component while everything else in the template is a function.
//
// The fallback is token-styled and offers a retry: reset clears the error and
// remounts the children. Changing `resetKey` (e.g. the route path) also clears
// a caught error, so navigating away from a crashed view recovers it.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './Button'
import { IconAlert, IconRefresh } from './icons'
import css from './ErrorBoundary.module.css'

export type ErrorBoundaryProps = {
  children: ReactNode
  /** When this value changes, a caught error is cleared automatically. */
  resetKey?: unknown
  /** Custom fallback; receives the error and a retry callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

type ErrorBoundaryState = {
  error: Error | null
}

/**
 * Wrap a subtree in an error boundary.
 * @returns the children, or the fallback once an error is caught.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replace with your telemetry sink; never swallow silently in production.
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.reset()
    }
  }

  /** Clear the caught error and remount the children. */
  reset = (): void => { this.setState({ error: null }) }

  render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children
    if (this.props.fallback !== undefined) return this.props.fallback(error, this.reset)
    return (
      <div className={css.fallback} role="alert">
        <span className={css.icon}><IconAlert size={28} /></span>
        <h2 className={css.title}>页面出错了</h2>
        <p className={css.message}>{error.message || '渲染时发生未知错误。'}</p>
        <Button
          variant="primary"
          size="sm"
          icon={<IconRefresh size={16} />}
          onClick={this.reset}
        >
          重试
        </Button>
      </div>
    )
  }
}
